<template>
  <CCardBody>
    <CDataTable
      class="custom-pagination"
      :items="ticketsData"
      :fields="tableFields"
      :table-filter="{ external: true, placeholder: $t('table.user.placeholderSearch') || 'ค้นหาเลขที่ใบงาน, รายละเอียด...', label: 'ค้นหา:' }"
      :table-filter-value="searchKeyword"
      @update:table-filter-value="onSearchChange"
      hover
      :pagination="false"
      :loading="isLoading"
      :items-per-page-select="{
        label: $t('table.common.itemsPerPage') || 'แสดงต่อหน้า:',
        values: [10, 20, 50],
        external: true
      }"
      :items-per-page="pageSize"
      @pagination-change="onPageSizeChange"
    >
      
      <template #ticketNumber="{ item }">
        <td>
          <div class="font-weight-bold text-primary">{{ item.ticketNumber || '-' }}</div>
        </td>
      </template>

      <template #reported_datetime="{ item }">
        <td>
          <div>{{ formatDate(item.userPart.dateTimeInformed) }}</div>
        </td>
      </template>

      <template #issue_user="{ item }">
        <td>
          <div class="font-weight-bold">{{ item.username }}</div>
          <div v-if="item.onBehalfOf" class="small text-danger">
            ({{ $t('table.admin.onBehalf') }} {{ item.onBehalfOf }})
          </div>
          <div class="small text-muted">
            {{ getCategoryName(item) }} | {{ getSubCategoryName(item) }}
          </div>
        </td>
      </template>

      <template #status="{ item }">
        <td class="text-center">
          <span class="status-circle" :style="{ backgroundColor: getBadge(item.status) }"
            :title="getStatusText(item.status)"></span>
        </td>
      </template>
      <template #show_details="{ item }">
        <td class="text-center">
          <div class="action-buttons">
            <button class="circle-action-btn" :title="$t('table.admin.btn.detail')" @click="showDetail(item, false)">
              <CIcon name="cil-window-restore" size="xl" />
            </button>
            
            <button 
              v-if="isOtherCategoryNeedsAssignment(item)"
              class="circle-action-btn assign-btn" 
              :title="$t('table.admin.btn.assignDept')" 
              @click="assignDepartment(item)"
            >
              <CIcon name="cil-task" size="xl" />
            </button>
          </div>
        </td>
      </template>
      
    </CDataTable>
    <CPagination
      v-show="totalPages > 1"
      :pages="totalPages"
      :active-page.sync="currentPage"
      :double-arrows="false"
      align="center"
    />
    <div class="status-legend mt-3">
      <div class="legend-item">
        <span class="status-circle" style="background:#e55353"></span>
        <span>{{ $t('table.admin.legend1') }}</span>
      </div>
      <div class="legend-item">
        <span class="status-circle" style="background:#fd7e14"></span>
        <span>{{ $t('table.admin.legend2') }}</span>
      </div>
      <div class="legend-item">
        <span class="status-circle" style="background:#ffeb3b"></span>
        <span>{{ $t('table.admin.legend3') }}</span>
      </div>
      <div class="legend-item">
        <span class="status-circle" style="background:#2eb85c"></span>
        <span>{{ $t('table.admin.legend4') }}</span>
      </div>
      <div class="legend-item">
        <span class="status-circle" style="background:#cdcdcd"></span>
        <span>{{ $t('table.admin.legend5') }}</span>
      </div>
    </div>
  </CCardBody>
</template>

<style>
/* CSS เดิม (ไม่เปลี่ยนแปลง) */
.custom-pagination .page-item.active .page-link {
  background-color: #2ba9a5 !important;
  border-color: #2ba9a5 !important;
  color: #ffffff;
}
.custom-pagination .page-item .page-link {
  color: #414141;
}
.custom-pagination .page-item:not(.active) .page-link:hover {
  background-color: #f0f0f0;
}
.circle-action-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid #321fdb; /* CoreUI primary */
  background: transparent;
  color: #321fdb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 0 2px;
}

.circle-action-btn:hover {
  background-color: #321fdb;
  color: #ffffff;
}

.circle-action-btn.assign-btn {
  border-color: #ffc107;
  color: #ffc107;
}

.circle-action-btn.assign-btn:hover {
  background-color: #ffc107;
  color: #ffffff;
}

.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.circle-action-btn i {
  font-size: 16px;
}

.status-circle {
  display: inline-block;
  width: 30px;
  height: 30px;
  border-radius: 50%;
}

.status-legend {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
}

.status-legend .status-circle {
  width: 14px;
  height: 14px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #414141;
}
</style>

<script>
import axios from "axios";
import { getSocket } from "../../service/socketio.service";

export default {
  name: "Areportable",
  data() {
    const saved = Number(localStorage.getItem('smartfix_page_size'));
    const initialPageSize = [10, 20, 50].includes(saved) ? saved : 50;
    return {
      ticketsData: [],
      isLoading: false,
      currentPage: 1,
      pageSize: initialPageSize,
      totalItems: 0,
      searchKeyword: "",
      searchTimeout: null,
      isMounted: false,
    };
  },
  computed: {
    tableFields() {
      return [
        { key: "ticketNumber", label: this.$t("table.user.col.ticketNo"), _style: "width:15%;" },
        { key: "reported_datetime", label: this.$t("table.user.col.reportedAt"), _style: "width:15%;" },
        { key: "issue_user", label: this.$t("table.admin.col.reporterDetail"), _style: "width:40%;" },
        {
          key: "status",
          label: this.$t("table.user.col.status"),
          _style: "width:15%; text-align:center;",
          _classes: "text-center",
        },
        { key: "show_details", label: this.$t("table.admin.col.actions"), _style: "width:15%; text-align:center" },
      ];
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.pageSize) || 1;
    },
  },
  mounted() {
    this.isMounted = true;
    this.loadTickets();
  },
  watch: {
    currentPage() {
      if (this.isMounted) this.loadTickets();
    },
  },
  methods: {
    onPageSizeChange(newSize) {
      const size = Number(newSize);
      if ([10, 20, 50].includes(size) && size !== this.pageSize) {
        this.pageSize = size;
        localStorage.setItem('smartfix_page_size', String(size));
        this.currentPage = 1;
        this.loadTickets();
      }
    },

    onSearchChange(val) {
      this.searchKeyword = val || "";
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadTickets();
      }, 350);
    },

    async loadTickets() {
      try {
        this.isLoading = true;
        const params = {
          status: 'active',
          page: this.currentPage,
          limit: this.pageSize,
        };
        if (this.searchKeyword && this.searchKeyword.trim()) {
          params.search = this.searchKeyword.trim();
        }

        const response = await axios.get(`${process.env.VUE_APP_APP_URL}/ticket/admin/all`, { params });

        if (response.data.success) {
          const allTickets = (response.data.data || []).map((ticket) => {
             const userPart = ticket.userPart || {};
             const user = userPart.user || {};

             return {
                ...ticket,
                username: `${user.name || ''} ${user.surname || ''}`.trim() || this.$t('table.admin.noUserName'),
                onBehalfOf: userPart.onBehalfOf || ''
             };
          });

          this.ticketsData = (this.searchKeyword && this.searchKeyword.trim())
            ? allTickets
            : allTickets.filter(t => t.status !== 'welldone');
          this.totalItems = response.data.pagination?.total ?? this.ticketsData.length;
        }
      } catch (error) {
        console.error("Failed to load admin tickets:", error);
      } finally {
        this.isLoading = false;
      }
    },

    refreshData() {
      this.loadTickets();
    },

    showDetail(item) {
      this.$emit("show-detail", item);
    },

    assignDepartment(item) {
      this.$emit("assign-department", item);
    },

    isOtherCategoryNeedsAssignment(item) {
      return (
        item.userPart &&
        item.userPart.categoryType === 'other' &&
        item.status === 'assigned' &&
        (!item.userPart.adminAssignment || !item.userPart.category)
      );
    },

    formatDate(dateString) {
      if(!dateString) return "-";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}${this.$t('table.user.timeSuffix')}`;
    },

    getCategoryName(item) {
      return item.userPart?.category?.departmentName || this.$t("table.user.unknownDept");
    },

    getSubCategoryName(item) {
      if (item.userPart && item.userPart.categoryType === 'other') {
        const isAssigned = item.userPart.adminAssignment && item.userPart.category;
        if (isAssigned) {
          return item.userPart.subCategory + this.$t("table.admin.subAssigned");
        } else {
          return (item.userPart.otherCategoryText || this.$t("common.other")) + this.$t("table.admin.subWaitAssign");
        }
      }

      const sub = item.userPart?.subCategory;
      
      if (!sub) return "-";

      if (typeof sub === 'object') {
        return sub.subCategory || sub.name || sub.title || sub.subcategoryName || "-";
      }

      return sub;
    },

    getStatusText(status) {
      const statusMap = {
        pending: this.$t("table.admin.status.pending"),
        assigned: this.$t("table.admin.status.assigned"),
        in_progress: this.$t("table.admin.status.in_progress"),
        done: this.$t("table.admin.status.done"),
        welldone: this.$t("table.admin.status.welldone"),
        cancelled: this.$t("table.admin.status.cancelled"),
      };
      return statusMap[status] || status;
    },

    getBadge(status) {
      const badgeMap = {
        pending: "#e55353",    
        assigned: "#fd7e14",   
        in_progress: "#ffeb3b",
        done: "#2eb85c",       
        welldone: "#2eb85c",
        // แสดงสถานะที่ถูกยกเลิกเป็นสีขาว
        cancelled: "#cdcdcd",
      };
      return badgeMap[status] || "#f8f9fa";
    },
  },
};
</script>
